import { Polygon, Position } from "geojson";

export function focusAreaFormatParameters(
  officeIds: string[] | null,
  agentIds: string[] | null,
  entityType: "office" | "agent" | null
): Record<string, unknown> {
  const params = { nestedFilters: [] };

  // Handle the case where no selection is made.
  if (
    !entityType &&
    (!officeIds || officeIds.length === 0) &&
    (!agentIds || agentIds.length === 0)
  ) {
    return params;
  }

  // Handle the case where no entity type is specified.
  if (!entityType) {
    if (officeIds && officeIds.length > 0) {
      params.nestedFilters.push({
        key: "belongsToOffice",
        filters: [
          {
            field: "id",
            operator: "anyOf",
            value: officeIds,
          },
        ],
      });
    }
    if (agentIds && agentIds.length > 0) {
      params.nestedFilters.push({
        key: "belongsToAgent",
        filters: [
          {
            field: "id",
            operator: "anyOf",
            value: agentIds,
          },
        ],
      });
    }
  } else {
    // Handle the case where an entity type is specified.
    if (entityType === "office") {
      if (!officeIds || officeIds.length === 0) {
        params.nestedFilters.push({
          key: "belongsToOffice",
          filters: [
            {
              field: "id",
              operator: "notEmpty",
            },
          ],
        });
      } else {
        params.nestedFilters.push({
          key: "belongsToOffice",
          filters: [
            {
              field: "id",
              operator: "anyOf",
              value: officeIds,
            },
          ],
        });
      }

      params.nestedFilters.push({
        key: "belongsToAgent",
        filters: [
          {
            field: "id",
            operator: "empty",
          },
        ],
      });
    } else if (entityType === "agent") {
      if (!agentIds || agentIds.length === 0) {
        params.nestedFilters.push({
          key: "belongsToAgent",
          filters: [
            {
              field: "id",
              operator: "notEmpty",
            },
          ],
        });

        if (officeIds && officeIds.length > 0) {
          params.nestedFilters.push({
            key: "belongsToOffice",
            filters: [{ field: "id", operator: "anyOf", value: officeIds }],
          });
        }
      } else {
        params.nestedFilters.push({
          key: "belongsToAgent",
          filters: [
            {
              field: "id",
              operator: "anyOf",
              value: agentIds,
            },
          ],
        });
      }
    }
  }

  return params;
}

// Translation keys returned by `validatePolygonRing`, so callers can toast
// them straight through the `translate` pipe.
export const POLYGON_ERROR_TOO_FEW_VERTICES =
  "sales_area_error_too_few_vertices";
export const POLYGON_ERROR_SELF_INTERSECTING =
  "sales_area_error_self_intersecting";

// The geometry checks below are ported from the customer portal's polygon
// editor (customer-portal/src/components/MapEditor/PolygonDrawing/internal/
// utils.ts) so both products reject the same invalid shapes. Keep them in
// sync when either side changes.

const cross = (p1: Position, p2: Position, p3: Position): number =>
  (p2[0] - p1[0]) * (p3[1] - p1[1]) - (p2[1] - p1[1]) * (p3[0] - p1[0]);

// Proper (non-touching) crossing of segments a-b and c-d. Shared endpoints
// and collinear overlaps deliberately don't count, so adjacent edges of a
// ring never register as an intersection.
const segmentsCross = (
  a: Position,
  b: Position,
  c: Position,
  d: Position
): boolean => {
  const d1 = cross(c, d, a);
  const d2 = cross(c, d, b);
  const d3 = cross(a, b, c);
  const d4 = cross(a, b, d);

  return (
    ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
    ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))
  );
};

// Removes consecutive duplicate vertices (within `epsilon` degrees) and
// re-closes the ring if the closing point was lost in dedup. Google Maps
// registers a double-click while drawing as two vertices at the same spot,
// which would otherwise read as a degenerate zero-length edge.
export function dedupeRing(ring: Position[], epsilon = 1e-9): Position[] {
  if (ring.length < 2) {
    return ring;
  }

  const eq = (a: Position, b: Position) =>
    Math.abs(a[0] - b[0]) <= epsilon && Math.abs(a[1] - b[1]) <= epsilon;

  const result: Position[] = [ring[0]];
  for (let i = 1; i < ring.length; i++) {
    if (!eq(ring[i], result[result.length - 1])) {
      result.push(ring[i]);
    }
  }

  if (result.length > 1 && !eq(result[0], result[result.length - 1])) {
    result.push(result[0]);
  }

  return result;
}

// True when any pair of non-adjacent edges cross each other, i.e. the ring
// is a "crossed"/bow-tie polygon rather than a simple one. Closes open rings
// itself so callers don't have to.
export function hasSelfIntersection(ring: Position[]): boolean {
  if (ring.length < 4) {
    return false;
  }

  const closed =
    ring[0][0] === ring[ring.length - 1][0] &&
    ring[0][1] === ring[ring.length - 1][1]
      ? ring
      : [...ring, ring[0]];
  const n = closed.length - 1;

  for (let i = 0; i < n; i++) {
    const a = closed[i];
    const b = closed[i + 1];

    for (let j = i + 2; j < n; j++) {
      // Skip the wrap-around pair: the last edge shares a vertex with the
      // first, so they are adjacent rather than crossing.
      if (i === 0 && j === n - 1) {
        continue;
      }

      if (segmentsCross(a, b, closed[j], closed[j + 1])) {
        return true;
      }
    }
  }

  return false;
}

// Returns a translation key describing why the ring is unusable as a sales
// area, or null when it is a valid simple polygon. Accepts open or closed
// rings in [lng, lat] order.
export function validatePolygonRing(ring: Position[]): string | null {
  const cleanRing = dedupeRing(ring ?? []);

  // A closed ring needs 4 entries to describe 3 distinct vertices.
  if (cleanRing.length < 4) {
    return POLYGON_ERROR_TOO_FEW_VERTICES;
  }

  if (hasSelfIntersection(cleanRing)) {
    return POLYGON_ERROR_SELF_INTERSECTING;
  }

  return null;
}

// Function to calculate the signed area of a ring
function calculateSignedArea(ring: Position[]): number {
  let area = 0;
  const n = ring.length;

  for (let i = 0; i < n - 1; i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[i + 1];
    area += (x2 - x1) * (y2 + y1);
  }

  return area / 2;
}

// Function to ensure the coordinates of the polygon are ordered clockwise
export function ensureClockwise(polygon: Polygon): Polygon {
  const adjustedCoordinates = polygon.coordinates.map((ring) => {
    const area = calculateSignedArea(ring);
    // If the area is positive, the ring is counterclockwise, so reverse it
    return area > 0 ? ring.slice().reverse() : ring;
  });

  return {
    ...polygon,
    coordinates: adjustedCoordinates,
  };
}
